import { $ } from '../lib/dom.js';
import { ct } from '../lib/i18n.js';
import { api } from '../lib/api.js';
import { trackDirtyForm } from '../lib/dirty-form.js';
import { findSelectMenu } from '../lib/select-menu.js';
import { toast } from '../lib/toast.js';

/**
 * The Verification page.
 *
 * A single form, and therefore a single save bar: switching tab never loses a
 * change in progress.
 */
export function initVerificationPage(): void {
  const found = $<HTMLFormElement>('[data-verification-form]');
  if (!found) return;

  const form: HTMLFormElement = found;
  const guildId = form.dataset.guildId ?? '';

  /*
   * Lifted out of `trackDirtyForm` so the "Publish" button can call it too.
   * Publishing reads the **saved** settings: without this you fill in the form,
   * click Publish, and the server answers on the old state — often empty, hence
   * "verification is disabled".
   */
  const save = async () => {
    await api.put(`/api/verification?guildId=${guildId}`, {
      guildId,
      enabled: checkbox(form, 'enabled'),

      channelId: findSelectMenu(form, 'channelId')?.single ?? '',
      kind: radio(form, 'kind', 'button'),

      grantRoleId: findSelectMenu(form, 'grantRoleId')?.single ?? '',
      removeRoleId: findSelectMenu(form, 'removeRoleId')?.single ?? '',

      title: text(form, 'title'),
      description: text(form, 'description'),
      buttonLabel: text(form, 'buttonLabel'),

      question: text(form, 'question'),
      answer: text(form, 'answer'),

      minAccountAgeDays: number(form, 'minAccountAgeDays'),
      maxAttempts: number(form, 'maxAttempts') || 3,

      onFailure: radio(form, 'onFailure', 'none'),
      failureDurationSeconds: number(form, 'failureDurationSeconds'),

      graceMinutes: number(form, 'graceMinutes'),
      onGraceExpiry: radio(form, 'onGraceExpiry', 'none'),
    });
  };

  const { markClean, isDirty } = trackDirtyForm({
    form,
    successMessage: ct('settingsSaved', 'Settings saved.'),
    onSave: save,
  });

  /*
   * What publishing needs, said before the attempt.
   *
   * The button used to be live whatever the state, and the refusal arrived as a
   * red message once the reader believed the thing done — naming, until now, a
   * field that lived on another tab. Both fields sit above this button now, and
   * the button says which of them is still empty rather than waiting to be
   * pressed to say it.
   */
  const publishButton = $<HTMLButtonElement>('[data-publish-portal]', form);
  const blocked = $<HTMLElement>('[data-publish-blocked]', form);
  const ready = $<HTMLElement>('[data-publish-ready]', form);

  const checkPublishable = () => {
    if (!publishButton) return;

    const missing =
      !findSelectMenu(form, 'channelId')?.single || !findSelectMenu(form, 'grantRoleId')?.single;

    publishButton.disabled = missing;
    if (blocked) blocked.hidden = !missing;
    if (ready) ready.hidden = missing;
  };

  /*
   * `change` on the form rather than on each menu: the select widgets are built
   * by the bundle after this runs, so anything bound to them by name here would
   * bind to nothing.
   */
  form.addEventListener('change', checkPublishable);
  checkPublishable();

  /*
   * The portal, as it will look.
   *
   * Two servers republished theirs nine and eight times in a row before
   * removing the bot: publishing was the only way to see it. Change a word,
   * publish, switch to Discord, look, come back. The loop is the best
   * documented gesture of the two most engaged departures this bot has had.
   *
   * The empty-field wording is the server's own defaults, handed down by the
   * view from what `buildPortal` actually uses — placeholders would show the
   * reader's language and lie to half the administrators.
   */
  const preview = $<HTMLElement>('[data-portal-preview]', form);

  if (preview) {
    const defaults = JSON.parse(preview.dataset.defaults ?? '{}') as {
      title?: string;
      description?: string;
      button?: string;
    };

    const paint = () => {
      const title = $<HTMLElement>('[data-preview-title]', preview);
      const description = $<HTMLElement>('[data-preview-description]', preview);
      const button = $<HTMLElement>('[data-preview-button]', preview);

      // `textContent` throughout: every value here is typed by an administrator
      // and lands on a page that shows the server's own name beside it.
      if (title) title.textContent = text(form, 'title') || (defaults.title ?? '');
      if (description) {
        description.textContent = text(form, 'description') || (defaults.description ?? '');
      }
      if (button) button.textContent = text(form, 'buttonLabel') || (defaults.button ?? '');
    };

    for (const name of ['title', 'description', 'buttonLabel']) {
      form.querySelector(`[name="${name}"]`)?.addEventListener('input', paint);
    }

    paint();
  }

  /*
   * Fields that only one mode reads.
   *
   * The question and its answer are asked in `question` mode alone — the button
   * mode grants the role on the click, the captcha shows an image. The form
   * showed all three sets at once and saved all three, so a server owner in
   * button mode could write a security question, its answer, publish, and never
   * be asked it. One did: fifteen minutes of it, then the bot was removed.
   *
   * The `hidden` attribute rather than a class: the production policy drops
   * inline styles, and a rule in the sheet would be a second place to keep in
   * step with the modes.
   */
  const showFieldsForKind = () => {
    const kind = radio(form, 'kind', 'button');

    for (const group of form.querySelectorAll<HTMLElement>('[data-when-kind]')) {
      group.hidden = group.dataset.whenKind !== kind;
    }
  };

  for (const input of form.querySelectorAll<HTMLInputElement>('[name="kind"]')) {
    input.addEventListener('change', showFieldsForKind);
  }

  showFieldsForKind();

  /*
   * Publishing is an action, not a setting: it goes out at once and does not
   * wait for the save bar. It saves first when the form has changed, because
   * the server publishes what it holds in the database, not what is on screen.
   */
  publishButton?.addEventListener('click', () => {
    void (async () => {
      try {
        // Save first when needed: what gets published is what is on screen, not
        // what was lingering in the database.
        if (isDirty()) {
          await save();
          markClean();
        }

        await api.post(`/api/verification/publish?guildId=${guildId}`, {});
        toast(ct('portalPublished', 'Portal published.'), 'success');
      } catch (error) {
        toast(error instanceof Error ? error.message : String(error), 'error');
      }
    })();
  });
}

function text(form: HTMLFormElement, name: string): string {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement || node instanceof HTMLTextAreaElement
    ? node.value.trim()
    : '';
}

function number(form: HTMLFormElement, name: string): number {
  const node = form.elements.namedItem(name);
  const value = node instanceof HTMLInputElement ? Number(node.value) : 0;
  return Number.isFinite(value) && value >= 0 ? value : 0;
}

function checkbox(form: HTMLFormElement, name: string): boolean {
  const node = form.elements.namedItem(name);
  return node instanceof HTMLInputElement ? node.checked : false;
}

/** The checked value of a group of option cards. */
function radio(form: HTMLFormElement, name: string, fallback: string): string {
  const node = form.elements.namedItem(name);
  if (node instanceof RadioNodeList) return node.value || fallback;
  return node instanceof HTMLInputElement ? node.value || fallback : fallback;
}
